import type { AccountView } from './store';
import { store } from './store';
import { navigateToView } from './navigation';
import { readReturnToFromStorage } from './returnToStorage';
import { getConfig } from './config';
import { isInlineViewOnCurrentPage, preferAuthModalOnCurrentPage } from './inlinePages';
import { authReturnParam } from './authReturnQuery';

const RETURN_PAGE_KEY = 'sw_return_page';
const REFERRAL_SHARE_RETURN_KEY = 'sw_referral_share_return';

export function markReferralShareReturn(): void {
  try { sessionStorage.setItem(REFERRAL_SHARE_RETURN_KEY, '1'); } catch { /* unavailable */ }
}

export function clearReferralShareReturn(): void {
  try { sessionStorage.removeItem(REFERRAL_SHARE_RETURN_KEY); } catch { /* unavailable */ }
}

function consumeReferralShareReturn(): boolean {
  try {
    const pending = sessionStorage.getItem(REFERRAL_SHARE_RETURN_KEY) === '1';
    sessionStorage.removeItem(REFERRAL_SHARE_RETURN_KEY);
    return pending;
  } catch { return false; }
}

function normalizePathOnly(path: string): string {
  return (path.split('?')[0] || '/').replace(/\/+$/, '') || '/';
}

export function isAuthRoutePath(path: string): boolean {
  const norm = normalizePathOnly(path);
  return norm === '/login' || norm === '/signup' || norm === '/reset-password';
}

/** Unwrap nested ?return= chains; never use /login or /signup as a return target. */
export function normalizeAuthReturnPath(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let current = raw.trim();
  if (!current) return null;

  for (let depth = 0; depth < 8; depth++) {
    if (!current.startsWith('/') || current.startsWith('//')) {
      try {
        const url = new URL(current, window.location.origin);
        if (url.origin !== window.location.origin) return null;
        current = url.pathname + url.search + url.hash;
      } catch {
        return null;
      }
    }

    if (!isAuthRoutePath(current)) return current;

    try {
      const url = new URL(current, window.location.origin);
      const nested = authReturnParam(url.search);
      if (!nested) return null;
      current = nested.trim();
    } catch {
      return null;
    }
  }
  return null;
}

function resolveReturnPathForAuthLink(explicit?: string): string | null {
  const direct = normalizeAuthReturnPath(explicit);
  if (direct) return direct;

  const returnToView = store.getReturnTo();
  if (returnToView && !isAuthFlowView(returnToView)) {
    const page = resolveViewPageUrl(returnToView);
    const fromView = normalizeAuthReturnPath(page);
    if (fromView) return fromView;
  }

  try {
    const fromStorage = normalizeAuthReturnPath(sessionStorage.getItem(RETURN_PAGE_KEY));
    if (fromStorage) return fromStorage;
  } catch { /* ignore */ }

  return normalizeAuthReturnPath(getCurrentReturnPath());
}

export function getCurrentReturnPath(): string {
  if (typeof window === 'undefined') return '/';
  if (window.location.pathname.includes('/preview-proxy')) {
    const p = new URLSearchParams(window.location.search).get('path') ?? '/';
    return p.startsWith('/') ? p : `/${p}`;
  }
  if (isAuthRoutePath(window.location.pathname)) {
    const fromUrl = authReturnParam(window.location.search);
    const normalized = normalizeAuthReturnPath(fromUrl);
    if (normalized) return normalized;
    return '/';
  }
  return window.location.pathname + window.location.search + window.location.hash;
}

/** Remember current page so post-login can return here (contact form, testimonials, etc.). */
export function rememberAuthReturnPage(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (window.location.pathname.includes('/preview-proxy')) {
    const currentPath = url.searchParams.get('path') ?? '/';
    url.searchParams.set('sw_return_path', currentPath);
    window.history.replaceState({}, '', url.toString());
    return;
  }
  try {
    sessionStorage.setItem(RETURN_PAGE_KEY, normalizeAuthReturnPath(getCurrentReturnPath()) ?? getCurrentReturnPath());
  } catch { /* ignore */ }
}

/** Read ?return= or backend ?redirect= from auth URL into sessionStorage (same-origin paths only). */
export function captureAuthReturnFromUrl(): void {
  if (typeof window === 'undefined') return;
  const raw = authReturnParam(window.location.search);
  if (!raw) return;
  const normalized = normalizeAuthReturnPath(raw);
  if (!normalized) return;
  try {
    sessionStorage.setItem(RETURN_PAGE_KEY, normalized);
  } catch { /* ignore */ }
}

function shouldOpenAuthAsModal(view: 'checkout-login' | 'signup'): boolean {
  if (preferAuthModalOnCurrentPage()) return true;
  // Modal unless this page actually renders the inline auth placeholder (not just configured shop-wide).
  return !isInlineViewOnCurrentPage(view);
}

export function buildAuthPageUrl(view: 'checkout-login' | 'signup', returnPath?: string): string | null {
  if (typeof window === 'undefined') return null;
  const { viewUrls } = getConfig();
  const base = String(viewUrls?.[view] ?? '').trim();
  if (base === '') return null;
  try {
    const url = new URL(base, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    const ret = resolveReturnPathForAuthLink(returnPath);
    if (ret) url.searchParams.set('return', ret);
    const cur = new URL(window.location.href);
    for (const key of ['lang', 'cur']) {
      const v = cur.searchParams.get(key);
      if (v && !url.searchParams.has(key)) url.searchParams.set(key, v);
    }
    return url.pathname + url.search + url.hash;
  } catch {
    return null;
  }
}

function openContextualAuthView(view: 'checkout-login' | 'signup', returnView?: AccountView): void {
  rememberAuthReturnPage();
  if (returnView !== undefined) {
    store.setReturnTo(returnView);
  }

  if (shouldOpenAuthAsModal(view)) {
    navigateToView(view, { inline: true });
    return;
  }

  const href = buildAuthPageUrl(view);
  if (href) {
    window.location.href = href;
    return;
  }
  navigateToView(view, { inline: true });
}

/** Login from current page; optional account view after auth (orders, bonuses, …). */
export function openContextualLogin(returnView?: AccountView): void {
  openContextualAuthView('checkout-login', returnView);
}

/** Signup from current page; optional account view after registration. */
export function openContextualSignup(returnView?: AccountView): void {
  openContextualAuthView('signup', returnView);
}

function consumeAuthReturnPage(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = sessionStorage.getItem(RETURN_PAGE_KEY);
    sessionStorage.removeItem(RETURN_PAGE_KEY);
    return normalizeAuthReturnPath(v) ?? (v || null);
  } catch {
    return null;
  }
}

function resolvePostAuthLandingUrl(): string {
  const { viewUrls } = getConfig();
  return viewUrls?.home || viewUrls?.catalog || '/';
}

function resolveViewPageUrl(view: NonNullable<AccountView>): string | null {
  const { viewUrls } = getConfig();
  const raw = viewUrls?.[view];
  if (!raw || typeof window === 'undefined') return null;
  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return url.pathname + url.search + url.hash;
  } catch {
    return raw.startsWith('/') ? raw : null;
  }
}

function isAuthFlowView(view: AccountView): boolean {
  return view === 'checkout-login' || view === 'signup' || view === 'reset-password'
    || view === 'checkout-auth' || view === 'forgot-password';
}

/** Hard leave /login, /signup, /reset-password after successful auth (inline page flow). */
export function completeAuthPageRedirect(): void {
  if (typeof window === 'undefined') return;

  if (consumeReferralShareReturn()) {
    clearReturnTo();
    consumeAuthReturnPage();
    const home = normalizeAuthReturnPath(getConfig().viewUrls?.home || '/') || '/';
    window.location.replace(home.split('#')[0] + '#referral');
    return;
  }

  const returnToView = resolvePostAuthReturnTo();
  clearReturnTo();

  if (returnToView && !isAuthFlowView(returnToView)) {
    const target = resolveViewPageUrl(returnToView);
    if (target) {
      window.location.replace(target);
      return;
    }
    navigateToView(returnToView);
    return;
  }

  const returnPage = normalizeAuthReturnPath(consumeAuthReturnPage());
  if (returnPage && !isAuthRoutePath(returnPage)) {
    window.location.replace(returnPage);
    return;
  }

  window.location.replace(resolvePostAuthLandingUrl());
}

export function isCheckoutAuthContext(): boolean {
  if (typeof window === 'undefined') return false;
  const pathname = window.location.pathname;
  if (/\/checkout(?:\/|$)/i.test(pathname) || /\/cart\/checkout/i.test(pathname)) return true;
  if (pathname.includes('/preview-proxy')) {
    const p = (new URLSearchParams(window.location.search).get('path') ?? '/').replace(/\/+$/, '') || '/';
    return /^\/checkout(?:\/|$)/i.test(p) || /^\/cart\/checkout/i.test(p) || p === '/cart';
  }
  return false;
}

/** Keep checkout as post-auth target when guest is already in checkout/auth flow. */
export function ensureCheckoutReturnTo(): void {
  if (!store.getReturnTo() && isCheckoutAuthContext()) {
    store.setReturnTo('checkout');
  }
}

export function resolvePostAuthReturnTo(): AccountView {
  const explicit = store.getReturnTo() ?? readReturnToFromStorage();
  if (explicit) return explicit;
  if (isCheckoutAuthContext()) return 'checkout';
  return null;
}

export function clearReturnTo(): void {
  store.setReturnTo(null);
}

export function navigateAfterAuth(inline?: boolean): void {
  if (typeof window !== 'undefined' && isAuthRoutePath(window.location.pathname)) {
    completeAuthPageRedirect();
    return;
  }

  if (consumeReferralShareReturn()) {
    clearReturnTo();
    consumeAuthReturnPage();
    store.setAccountView(null);
    const home = normalizeAuthReturnPath(getConfig().viewUrls?.home || '/') || '/';
    const target = home.split('#')[0] + '#referral';
    if (window.location.pathname !== new URL(home, window.location.origin).pathname) {
      window.location.replace(target);
    } else {
      window.location.hash = 'referral';
    }
    return;
  }

  const returnToView = resolvePostAuthReturnTo();
  clearReturnTo();

  if (returnToView) {
    navigateToView(returnToView);
    return;
  }
  // Contextual pages (contact/testimonials): stay on page after modal login — never pop cart drawer.
  if (!inline && store.getCartCount() > 0 && !preferAuthModalOnCurrentPage()) {
    navigateToView('cart');
    return;
  }

  if (typeof window === 'undefined') {
    navigateToView(null);
    return;
  }

  const isPreviewProxy = window.location.pathname.includes('/preview-proxy');
  if (isPreviewProxy) {
    const returnPage = consumeAuthReturnPage();
    if (returnPage) {
      const u = new URL(window.location.href);
      const normReturn = (returnPage.split('?')[0] || '/').replace(/\/+$/, '') || '/';
      const currentPath = ((u.searchParams.get('path') ?? '/').split('?')[0] || '/').replace(/\/+$/, '') || '/';
      if (normReturn !== currentPath) {
        u.searchParams.set('path', normReturn);
        window.location.href = u.toString();
        return;
      }
    }
    const u = new URL(window.location.href);
    const currentPath = ((u.searchParams.get('path') ?? '/').split('?')[0] || '/').replace(/\/+$/, '') || '/';
    if (currentPath !== '/catalog') {
      u.searchParams.set('path', '/catalog');
      window.location.href = u.toString();
      return;
    }
    navigateToView(null);
    return;
  }

  const returnPage = normalizeAuthReturnPath(consumeAuthReturnPage());
  if (returnPage && !isAuthRoutePath(returnPage)) {
    const current = window.location.pathname + window.location.search + window.location.hash;
    if (returnPage !== current) {
      window.location.href = returnPage;
      return;
    }
  }

  navigateToView(null);
}
